import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Newsletter Section */}
      <div className="bg-secondary border-b border-foreground/10">
        <div className="container py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="font-display text-2xl lg:text-3xl mb-3 text-foreground">
              Get 20% Off Your First Booking
            </h3>
            <p className="text-muted-foreground mb-6">
              Join our newsletter for exclusive deals and island updates.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <label htmlFor="footer-newsletter-email" className="sr-only">
                Email address for newsletter
              </label>
              <input
                id="footer-newsletter-email"
                type="email"
                name="email"
                aria-label="Email address for newsletter"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl bg-background border border-foreground/20 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <Button variant="sunset" size="lg">
                Subscribe
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-3">
              No spam. Just deals and island updates.
            </p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div>
            <div className="mb-6">
              <span className="font-display text-2xl font-bold">Son's Island</span>
              <p className="text-xs text-primary-foreground/60 tracking-wider mt-1">
                PRIVATE ISLAND RESORT
              </p>
            </div>
            <div className="space-y-3 text-sm text-primary-foreground/70">
              <a
                href="tel:877-577-7667"
                className="flex items-center gap-2 hover:text-primary-foreground transition-colors"
              >
                <Phone className="w-4 h-4" />
                877-577-7667
              </a>
              <a
                href="mailto:info@sonsgetaways.com"
                className="flex items-center gap-2 hover:text-primary-foreground transition-colors"
              >
                <Mail className="w-4 h-4" />
                info@sonsgetaways.com
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5" />
                <span>
                  110 Lee St<br />
                  Seguin, TX 78155
                </span>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <a
                href="https://www.facebook.com/sonsislandfun/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our Facebook page"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/sons.island/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our Instagram page"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/c/SonsIsland"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit our YouTube channel"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-2 text-sm text-primary-foreground/70">
              <Link to="/book-now" className="hover:text-primary-foreground transition-colors">
                Book Now
              </Link>
              <Link to="/cabanas" className="hover:text-primary-foreground transition-colors">
                Cabana Rentals
              </Link>
              <Link to="/glamping" className="hover:text-primary-foreground transition-colors">
                Glamping
              </Link>
              <Link to="/activities" className="hover:text-primary-foreground transition-colors">
                Activities
              </Link>
              <Link to="/island-map" className="hover:text-primary-foreground transition-colors">
                Island Map
              </Link>
              <Link to="/faq" className="hover:text-primary-foreground transition-colors">
                FAQ
              </Link>
            </nav>
          </div>

          {/* More Links */}
          <div>
            <h4 className="font-semibold mb-4">More</h4>
            <nav className="flex flex-col gap-2 text-sm text-primary-foreground/70">
              <Link to="/about" className="hover:text-primary-foreground transition-colors">
                About Us
              </Link>
              <Link to="/specials" className="hover:text-primary-foreground transition-colors">
                Specials & Deals
              </Link>
              <Link to="/contact" className="hover:text-primary-foreground transition-colors">
                Contact
              </Link>
              <Link to="/waivers" className="hover:text-primary-foreground transition-colors">
                Waivers
              </Link>
              <Link to="/hours-directions" className="hover:text-primary-foreground transition-colors">
                Hours & Directions
              </Link>
            </nav>
          </div>

          {/* Other Properties */}
          <div>
            <h4 className="font-semibold mb-4">
              <a
                href="https://sonsgetaways.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Son's Island Getaways, LLC. Family
              </a>
            </h4>
            <nav className="flex flex-col gap-2 text-sm text-primary-foreground/70">
              <a
                href="https://sonsriocibolo.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Son's Rio Cibolo
              </a>
              <a
                href="https://sonsgeronimo.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Son's Geronimo
              </a>
              <a
                href="https://sonsriverranch.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Son's River Ranch
              </a>
              <a
                href="https://bluerivercamp.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Son's Blue River Camp
              </a>
              <a
                href="https://sonsguadalupe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Son's Guadalupe
              </a>
              <a
                href="https://riociboloranch.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-foreground transition-colors"
              >
                Rio Cibolo Ranch
              </a>
              <a
                href="https://sonsgetaways.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent/80 transition-colors mt-2"
              >
                View All Properties →
              </a>
            </nav>
          </div>
        </div>
      </div>

      {/* Popular Searches — SEO anchor block */}
      <div className="border-t border-primary-foreground/10">
        <div className="container py-8">
          <h4 className="font-semibold text-sm mb-3 text-primary-foreground/80">Popular Searches</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-primary-foreground/60">
            <Link to="/things-to-do-near-san-antonio" className="hover:text-primary-foreground transition-colors">Fun things to do near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/family-getaways-near-san-antonio" className="hover:text-primary-foreground transition-colors">Family getaway near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/kayaking-near-san-antonio" className="hover:text-primary-foreground transition-colors">Kayaking near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/paddle-boarding-near-san-antonio" className="hover:text-primary-foreground transition-colors">Paddle boarding near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/things-to-do-near-san-antonio" className="hover:text-primary-foreground transition-colors">Things to do with kids near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/glamping-near-san-antonio" className="hover:text-primary-foreground transition-colors">Glamping near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/cabana-rentals-san-antonio" className="hover:text-primary-foreground transition-colors">Cabana rentals near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/cabana-rentals-austin" className="hover:text-primary-foreground transition-colors">Cabana rentals near Austin</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/things-to-do-near-houston" className="hover:text-primary-foreground transition-colors">Things to do near Houston</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/other-properties" className="hover:text-primary-foreground transition-colors">Day trips from San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/other-properties" className="hover:text-primary-foreground transition-colors">Texas Hill Country getaways</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/blog" className="hover:text-primary-foreground transition-colors">San Antonio family blog</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/things-to-do-new-braunfels" className="hover:text-primary-foreground transition-colors">Things to do in New Braunfels</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/things-to-do-seguin" className="hover:text-primary-foreground transition-colors">Things to do in Seguin TX</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/day-trips-from-austin" className="hover:text-primary-foreground transition-colors">Day trips from Austin</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/things-to-do-san-marcos" className="hover:text-primary-foreground transition-colors">Things to do near San Marcos</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/tubing-near-san-antonio" className="hover:text-primary-foreground transition-colors">Tubing near San Antonio</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/glamping-austin" className="hover:text-primary-foreground transition-colors">Glamping near Austin</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/cabana-rentals-new-braunfels" className="hover:text-primary-foreground transition-colors">Cabana rentals New Braunfels</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/blog/glamping-near-san-antonio" className="hover:text-primary-foreground transition-colors">Glamping blog: weekend guide</Link>
            <span className="text-primary-foreground/20">·</span>
            <Link to="/blog/spring-break-sons-river-ranch" className="hover:text-primary-foreground transition-colors">Spring Break at Son's River Ranch</Link>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-primary-foreground/10">
        <div className="container py-6 text-center text-sm text-primary-foreground/50">
          © {new Date().getFullYear()} Son's Island – Managed by Son's Island Getaways, LLC.. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
